import { useState } from 'react';
import { useConnection, useSwitchChain } from 'wagmi';
import { stakingManagerAbi } from '@syntra/abi';
import { CHAIN } from '../../config/wagmi.ts';
import { DEPLOYMENT as D } from '../../config/env.ts';
import { useEpochStatus } from '../../hooks/useEpochStatus.ts';
import { useFee } from '../../hooks/useFee.ts';
import { useI18n } from '../../i18n/index.tsx';
import { fmtFee } from '../../lib/format.ts';
import { Button, Notice } from '../ui/primitives.tsx';
import { useToast } from '../ui/toasts.tsx';
import { useConnectModal } from '../layout/ConnectModal.tsx';
import { useTx } from './useTx.ts';

/**
 * Pays every finished epoch the keeper has not: `rebase()` as many times as it takes (each call processes at most
 * 30 epochs), with the progress on the button. Anyone may call it; it costs only the network fee.
 */
export function CatchUp({ onDone, variant = 'secondary' }: { onDone?: () => void; variant?: 'primary' | 'secondary' }) {
  const { t } = useI18n();
  const toast = useToast();
  const { isConnected, chainId } = useConnection();
  const { switchChain } = useSwitchChain();
  const openConnect = useConnectModal();
  const tx = useTx();
  const status = useEpochStatus();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fee = useFee({ address: D.staking, abi: stakingManagerAbi, functionName: 'rebase' }, isConnected && chainId === CHAIN.id && !progress);

  if (!isConnected) {
    return (
      <Button block variant={variant} onClick={openConnect}>
        {t('wallet.connect')}
      </Button>
    );
  }
  if (chainId !== CHAIN.id) {
    return (
      <Button block variant="secondary" onClick={() => switchChain({ chainId: CHAIN.id })}>
        {t('network.switch', { chain: CHAIN.name })}
      </Button>
    );
  }

  const run = async () => {
    const total = await status.refetch();
    if (total === 0) {
      onDone?.();
      return;
    }
    setProgress({ done: 0, total });
    let behind = total;
    while (behind > 0) {
      const hash = await tx.run({ address: D.staking, abi: stakingManagerAbi, functionName: 'rebase' });
      if (!hash) break; // the reason shows below
      behind = await status.refetch();
      setProgress({ done: total - behind, total });
    }
    setProgress(null);
    if (behind === 0) {
      toast('ok', t('epoch.caughtUp'));
      onDone?.();
    }
  };

  return (
    <div className="field catch-up" data-testid="catch-up">
      <Button block variant={variant} disabled={tx.busy} onClick={() => void run()}>
        {progress
          ? t('epoch.progress', { done: progress.done, total: progress.total })
          : tx.phase === 'confirm'
            ? t('common.confirmInWallet')
            : t('epoch.catchUp')}
      </Button>
      {fee !== null ? (
        <div className="muted fee-line" data-testid="fee">
          {t('common.fee', { amount: fmtFee(fee) })}
        </div>
      ) : null}
      {tx.error ? <Notice tone="error">{tx.error}</Notice> : null}
    </div>
  );
}
